import type { KeyboardEvent, ReactElement, RefObject } from 'react';

import { memo } from 'react';
import { useTheme } from 'styled-components';

import { S } from './accountMenuStyles';
import LoginIcon from '../../core/icons/LoginIcon';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

type AccountMenuTriggerProps = {
  href: string;
  iconAriaLabel: string;
  menuAriaLabel: string;
  isOpen: boolean;
  iconLinkRef: RefObject<HTMLAnchorElement | null>;
  toggleButtonRef: RefObject<HTMLButtonElement | null>;
  onLinkFocus: () => void;
  onLinkKeyDown: (event: KeyboardEvent<HTMLAnchorElement>) => void;
  onToggleClick: () => void;
  onToggleKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onIconMouseEnter?: () => void;
  onIconMouseMove?: () => void;
};

const AccountMenuTriggerComponent = ({
  href,
  iconAriaLabel,
  menuAriaLabel,
  isOpen,
  iconLinkRef,
  toggleButtonRef,
  onLinkFocus,
  onLinkKeyDown,
  onToggleClick,
  onToggleKeyDown,
  onIconMouseEnter,
  onIconMouseMove,
}: AccountMenuTriggerProps): ReactElement => {
  const theme = useTheme();
  const { track } = useTopNavTracking();

  return (
    <>
      <S.IconLink
        ref={iconLinkRef}
        href={href}
        aria-label={iconAriaLabel}
        onFocus={onLinkFocus}
        onKeyDown={onLinkKeyDown}
        onMouseEnter={onIconMouseEnter}
        onMouseMove={onIconMouseMove}
        onClick={() =>
          track({ event: 'header_interaction', element: 'account', interaction_type: 'click' })
        }
        data-testid="account-menu-login-link"
        data-cs-capture=""
      >
        <S.IconWrapper>
          <LoginIcon color={theme.colors.content.component.header.color} />
        </S.IconWrapper>
      </S.IconLink>

      <S.Toggle
        ref={toggleButtonRef}
        id="desktop-account-menu-toggle"
        type="button"
        aria-expanded={isOpen}
        aria-controls="desktop-account-menu-dropdown"
        aria-label={menuAriaLabel}
        onClick={onToggleClick}
        onKeyDown={onToggleKeyDown}
        data-testid="account-menu-toggle"
      />
    </>
  );
};

export const AccountMenuTrigger = memo(AccountMenuTriggerComponent);
